// Sub-build 2b — normalises scraped fit_type text into Fashion AI's
// modifier vocabulary. Mirrored at supabase/functions/_shared/fitTypeNormalisation.ts.
// Keep the two copies in sync.

export const FASHION_AI_FIT_TYPES = [
  "slim",
  "relaxed",
  "oversized",
  "cropped",
  "longline",
  "muscle",
] as const;
export type FashionAIFitType = (typeof FASHION_AI_FIT_TYPES)[number];

// Tier 1 normalisation map — high-confidence single-keyword matches.
const TIER1_KEYWORD_MAP: Record<string, FashionAIFitType> = {
  slim: "slim",
  fitted: "slim",
  skinny: "slim",
  oversized: "oversized",
  loose: "oversized",
  relaxed: "relaxed",
  boxy: "relaxed",
  cropped: "cropped",
  "crop top": "cropped",
  longline: "longline",
  tunic: "longline",
  muscle: "muscle",
  "muscle fit": "muscle",
  "muscle cut": "muscle",
  athletic: "muscle",
  "athletic fit": "muscle",
  "athletic cut": "muscle",
};

// Values that explicitly indicate NO modifier needed.
const NO_MODIFIER_VALUES = new Set([
  "regular",
  "regular fit",
  "standard",
  "true to size",
  "",
]);

export type NormalisationResult =
  | { matched: true; fit_type: FashionAIFitType; original: string }
  | {
      matched: false;
      reason: "no_modifier_needed" | "ambiguous" | "unknown";
      original: string;
    };

export function normaliseFitType(
  rawText: string | null | undefined,
): NormalisationResult {
  if (!rawText) {
    return { matched: false, reason: "no_modifier_needed", original: "" };
  }
  const lower = rawText.trim().toLowerCase();
  if (NO_MODIFIER_VALUES.has(lower)) {
    return { matched: false, reason: "no_modifier_needed", original: rawText };
  }

  const matchedTypes = new Set<FashionAIFitType>();
  for (const [keyword, fitType] of Object.entries(TIER1_KEYWORD_MAP)) {
    if (lower.includes(keyword)) matchedTypes.add(fitType);
  }

  if (matchedTypes.size > 1) {
    return { matched: false, reason: "ambiguous", original: rawText };
  }
  if (matchedTypes.size === 1) {
    const [fitType] = matchedTypes;
    return { matched: true, fit_type: fitType, original: rawText };
  }
  return { matched: false, reason: "unknown", original: rawText };
}

/**
 * Fire-and-forget logger for unmatched/ambiguous values. Never throws,
 * never blocks the main flow on failure.
 */
export async function logDataQualityIssue(
  // deno-lint-ignore no-explicit-any
  supabaseClient: any,
  params: {
    source: string;
    reason: string;
    value: string;
    retailer?: string | null;
  },
): Promise<void> {
  if (!supabaseClient) return;
  try {
    await supabaseClient
      .from("data_quality_log")
      .upsert(
        {
          source: params.source,
          reason: params.reason,
          value: params.value,
          retailer: params.retailer ?? null,
          count_seen: 1,
          last_seen: new Date().toISOString(),
        },
        { onConflict: "source,reason,value", ignoreDuplicates: false },
      );
  } catch (err) {
    console.warn("data_quality_log write failed:", err);
  }
}
